import { parseEuroToCents, type InvoiceInput, type InvoiceItem, type Lookups, type OrderListItem } from '@ft/shared';
import { useI18n } from '../i18n';
import { useCallback, useState } from 'react';
import { centsToField, dtfmt, todayIso } from '../lib/format';
import { api } from '../lib/http';
import { Modal } from './ui';

/**
 * Rechnung erfassen oder bearbeiten. `preset` füllt Bestellung/Projekt vor (z. B. aus der Bestellansicht).
 */
export function InvoiceForm({ invoice, preset, lookups, orders, onClose, onSaved, canDelete = false, paymentOnly = false }: {
  invoice: InvoiceItem | null;
  /** Finance: nur Zahlungsstatus, Fälligkeit, Bezahlt am und Warranty änderbar */
  paymentOnly?: boolean;
  /** Löschen anbieten (PM/Vertretung des Projekts, Admin, Delivery Head) */
  canDelete?: boolean;
  preset?: { orderId?: string; projectId?: string };
  lookups: Lookups;
  orders: OrderListItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, lang } = useI18n();
  const presetOrder = orders.find((o) => o.id === (invoice?.order?.id ?? preset?.orderId));
  const presetProject = lookups.projects.find((p) => p.id === (invoice?.project.id ?? preset?.projectId));
  const [customerId, setCustomerId] = useState(presetOrder?.customer.id ?? presetProject?.customerId ?? '');
  const [projectId, setProjectId] = useState(presetProject?.id ?? '');
  const [orderId, setOrderId] = useState(presetOrder?.id ?? '');
  const [invoiceNumber, setInvoiceNumber] = useState(invoice?.invoiceNumber ?? '');
  const [invoiceDate, setInvoiceDate] = useState(invoice?.invoiceDate ?? todayIso());
  const [amount, setAmount] = useState(invoice ? centsToField(invoice.amountCents) : '');
  // Zahlungsstatus von Hand ('' = unbekannt / nicht abgeglichen)
  const [payStatus, setPayStatus] = useState<'' | 'paid' | 'unpaid'>(invoice?.payment?.status ?? '');
  const [dueDate, setDueDate] = useState(invoice?.payment?.dueDate ?? '');
  const [paidAt, setPaidAt] = useState(invoice?.payment?.paidAt ?? todayIso());
  // Warranty: Umsatz ja, Forderung nein; neu vorbelegt, wenn die gewählte Bestellung eine Warranty-Bestellung ist
  const [warranty, setWarranty] = useState(invoice?.warranty ?? presetOrder?.kind === 'warranty');
  const payChanged = payStatus !== (invoice?.payment?.status ?? '') || (dueDate || '') !== (invoice?.payment?.dueDate ?? '') || (payStatus === 'paid' && paidAt !== (invoice?.payment?.paidAt ?? ''));
  const [busy, setBusy] = useState<'save' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cents = amount.trim() ? parseEuroToCents(amount, lang) : null;
  const projects = lookups.projects.filter((p) => p.customerId === customerId);
  const custOrders = orders.filter((o) => o.customer.id === customerId)
    .sort((a, b) => Number(b.projects.includes(projects.find((p) => p.id === projectId)?.name ?? '#')) - Number(a.projects.includes(projects.find((p) => p.id === projectId)?.name ?? '#')) || b.orderDate.localeCompare(a.orderDate));
  const close = useCallback(() => { if (!busy) onClose(); }, [busy, onClose]);

  async function save() {
    setError(null);
    if (!invoiceNumber.trim()) return setError(t('if.err.number'));
    if (!projectId) return setError(t('if.err.project'));
    if (cents === null || cents === 0) return setError(t('if.err.amount'));
    if (payStatus === 'paid' && !paidAt) return setError(t('if.err.paidAt'));
    const body: InvoiceInput = { version: invoice?.version, invoiceNumber: invoiceNumber.trim(), invoiceDate, projectId, orderId: orderId || null, amountCents: cents, warranty,
      ...(payChanged || (!invoice && payStatus) ? { paymentStatus: payStatus || null, dueDate: payStatus === 'unpaid' && dueDate ? dueDate : null, paidAt: payStatus === 'paid' && paidAt ? paidAt : null } : {}) };
    setBusy('save');
    try {
      if (invoice) await api('PUT', `/api/invoices/${invoice.id}`, body);
      else await api('POST', '/api/invoices', body);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  }

  async function remove() {
    if (!invoice) return;
    setBusy('delete');
    try {
      await api('DELETE', `/api/invoices/${invoice.id}?version=${invoice.version}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  }

  return (
    <Modal
      title={invoice ? t('if.title', { n: invoice.invoiceNumber }) : t('inv.add')}
      crumb={t('inv.title')}
      onClose={close}
      width={640}
      footer={<>
        {error && <p className="err" role="alert">{error}</p>}
        {invoice && canDelete && !confirmDelete && <button className="btn danger" onClick={() => setConfirmDelete(true)} disabled={!!busy}>{t('common.archive')}</button>}
        {invoice && canDelete && confirmDelete && <button className="btn danger" onClick={remove} disabled={!!busy}>{busy === 'delete' ? t('common.archiving') : t('common.archiveConfirm')}</button>}
        <button className="btn" onClick={close} disabled={!!busy}>{t('common.cancel')}</button>
        <button className="btn primary" onClick={save} disabled={!!busy}>{busy === 'save' ? t('common.saving') : t('common.save')}</button>
      </>}
    >
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {paymentOnly && <p className="help full">{t('if.financeOnly')}</p>}
        <div className="fld">
          <label htmlFor="if-no">{t('if.number')} <span className="req">*</span></label>
          <input id="if-no" disabled={paymentOnly} className="mono" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())} placeholder={t('if.numberPh')} autoComplete="off" />
        </div>
        <div className="fld">
          <label htmlFor="if-d">{t('if.date')} <span className="req">*</span></label>
          <input id="if-d" disabled={paymentOnly} type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} required />
        </div>
        <div className="fld">
          <label htmlFor="if-c">{t('common.customer')} <span className="req">*</span></label>
          <select id="if-c" disabled={paymentOnly} value={customerId} onChange={(e) => { setCustomerId(e.target.value); setProjectId(''); setOrderId(''); }}>
            <option value="">{t('common.choose')}</option>
            {lookups.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="if-p">{t('common.project')} <span className="req">*</span></label>
          <select id="if-p" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!customerId || paymentOnly}>
            <option value="">{customerId ? t('common.choose') : t('common.chooseCustomerFirst')}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="if-o">{t('inv.order')}</label>
          <select id="if-o" value={orderId} onChange={(e) => { setOrderId(e.target.value); if (!invoice && orders.find((o) => o.id === e.target.value)?.kind === 'warranty') setWarranty(true); }} disabled={!customerId || paymentOnly}>
            <option value="">{t('inv.withoutOrder')}</option>
            {custOrders.map((o) => <option key={o.id} value={o.id}>{o.orderNumber ?? t('common.noNumber')}{o.kind === 'warranty' ? ` · ${t('common.warranty')}` : ''} · {o.projects.join(', ')}</option>)}
          </select>
        </div>
        <div className="fld">
          <label htmlFor="if-a">{t('if.amount')} <span className="req">*</span></label>
          <input id="if-a" disabled={paymentOnly} className={`r${amount.trim() && cents === null ? ' bad' : ''}`} inputMode="decimal" value={amount} placeholder={lang === 'en' ? '0.00' : '0,00'}
            onChange={(e) => setAmount(e.target.value)} onBlur={() => { if (cents !== null) setAmount(centsToField(cents)); }} />
        </div>
        <div className="fld">
          <label htmlFor="if-ps">{t('if.payStatus')}</label>
          <select id="if-ps" value={payStatus} onChange={(e) => setPayStatus(e.target.value as '' | 'paid' | 'unpaid')}>
            <option value="">{t('pay.unknown')}</option>
            <option value="unpaid">{t('pay.unpaid')}</option>
            <option value="paid">{t('pay.paid')}</option>
          </select>
          <span className="help">{invoice?.payment ? (invoice.payment.source === 'manual' ? t('if.paySetManual', { at: invoice.payment.updatedAt ? dtfmt(invoice.payment.updatedAt) : '–' }) : t('if.paySetWorkday', { at: invoice.payment.updatedAt ? dtfmt(invoice.payment.updatedAt) : '–' })) : t('if.payHelp')}</span>
        </div>
        {payStatus === 'paid' && (
          <div className="fld">
            <label htmlFor="if-paid">{t('if.paidAt')} <span className="req">*</span></label>
            <input id="if-paid" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} required />
            <span className="help">{t('if.paidAtHelp')}</span>
          </div>
        )}
        {payStatus === 'unpaid' && (
          <div className="fld">
            <label htmlFor="if-due">{t('if.dueDate')}</label>
            <input id="if-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            <span className="help">{t('if.dueHelp')}</span>
          </div>
        )}
        <div className="fld full">
          <label className="checkline"><input type="checkbox" checked={warranty} onChange={(e) => setWarranty(e.target.checked)} />{t('if.warranty')}</label>
          <span className="help">{t('if.warrantyHelp')}</span>
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
